/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, effect } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { ProductPricesViewModel, RiskItem } from 'src/app/api';
import { ClimaticPeril } from 'src/app/api/model/climaticPerilVm';
import { ClimaticState, initialClimaticState } from 'src/app/core/climatic.store';

import { initialSummaryOfferState, SummaryOfferState } from 'src/app/core/summary-offer.store';
import { COMPARATOR, partners } from 'src/app/shared/constants/partner.constant';
import { Formule } from 'src/app/shared/enum/housing.enum';
import { SpaceOfRoom } from 'src/app/shared/enum/spaceOfRoom.enum';
import { Address } from 'src/app/shared/models/interface/concretes/address.interface';
import { KeyAmount } from 'src/app/shared/models/interface/concretes/key-amount.interface';
import { Lessor } from 'src/app/shared/models/interface/concretes/lessor.interface';
import { PackageVM } from 'src/app/shared/models/interface/concretes/offer-customize-custom.interface';
import { Option } from 'src/app/views/process/options/options/options.component';
import { environment } from 'src/environments/environment';
import { BDG, DOMX, OPT_AEXT, RCS, VAN, VLX } from '../shared/constants/options.constants';
import { getProductInfo } from '../shared/constants/product.constants';
import { DeductibleType, getDefaultDeductibleTypeByPath } from '../shared/enum/deductible-type.enum';
import { PathType } from '../shared/enum/path-type.enum';
import { isPartner } from '../shared/utils/brand-utils';
import { ContextState, initialContextState } from './context.store';
import { ContractState, initialContractState } from './contract.store';
import { DeclarationState, initialDeclarationState } from './declaration.store';
import { DevisState, initialDevisState } from './devis.store';
import { DirectDebitState, initialDirectDebitState } from './direct-debit.store';
import { ElectronicSignatureState, initialElectronicSignatureState } from './electronic-signature.store';
import {
  initialOfferState,
  initialOptionState,
  LessorAdditionalInformation,
  OfferState,
  optionItemState,
  SchoolInsuranceState,
} from './offer.store';
import { initialOptionLayoutState, OptionLayoutState } from './option-layout.store';
import { PhonePrefixState } from './phone-prefix.store';
import { initialSubscriptionState, SubscriptionState } from './steps.store';
import { initialSubscriberState, SubscriberState } from './subscribers.store';

export interface CombinedState {
  context: ContextState;
  path: PathType;
  contactPhone: string | null;
  option_layout: OptionLayoutState;
  declaration: DeclarationState;
  subscriber: SubscriberState;
  offer: OfferState;
  packages: Array<ProductPricesViewModel>;
  direct_debit: DirectDebitState;
  devis: DevisState;
  contract: ContractState;
  electronic_signature: ElectronicSignatureState;
  subscription: SubscriptionState | null;
  climatic: ClimaticState;
  summaryOffer: SummaryOfferState;
  forceAddressNavigation: boolean;
}

const initialState: CombinedState = {
  context: initialContextState,
  path: PathType.UNDEFINED,
  contactPhone: null,
  option_layout: initialOptionLayoutState,
  declaration: initialDeclarationState,
  packages: [],
  subscriber: initialSubscriberState,
  offer: initialOfferState,
  direct_debit: initialDirectDebitState,
  devis: initialDevisState,
  contract: initialContractState,
  electronic_signature: initialElectronicSignatureState,
  subscription: null,
  climatic: initialClimaticState,
  summaryOffer: initialSummaryOfferState,
  forceAddressNavigation: false,
};

export const CombinedStore = signalStore(
  { providedIn: 'root' },
  withState(getStateFromLocalStorage()),
  withComputed((store) => ({
    summary: computed(() => {
      const summary = [] as KeyAmount[];
      const offer = store.summaryOffer();
      const productInfo = getProductInfo(store.path());

      // Add base package if available
      if (offer.package) {
        summary.push({
          key: 'Assurance habitation ' + (productInfo?.name || ''),
          value: String(offer.package.monthlyPremiumAmountIncludingTax!),
        });
      }
      // Add options
      if (offer.options && offer.options.length > 0) {
        summary.push(...offer.options);
      }
      return summary;
    }),
    postalCode: computed(
      () => store.declaration().address?.addressAutocomplete?.zipCode ?? store.declaration().address?.zipCode,
    ),
    effect_date_formatted: computed(
      () => `${store.declaration.effect_date()?.toLocaleString('fr-FR').substring(0, 10)}`,
    ),
    birth_date_subscriber_formatted: computed(
      () => `${store.subscriber.birth_date()?.toLocaleString('fr-FR').substring(0, 10)}`,
    ),
    start_date_direct_debit_formatted: computed(
      () => `${store.direct_debit.start_date()?.toLocaleString('fr-FR').substring(0, 10)}`,
    ),
    productRecommended: computed<string | null>(() => {
      const insuredValue = store.declaration().capitalMobilier?.insuredValue ?? '';
      const reimbursementNewValue = store.declaration().capitalMobilier?.reimbursementNewValue ?? false;

      return insuredValue.startsWith('<') && reimbursementNewValue === false ? Formule.CONF : Formule.CONFPlus;
    }),

    getRoomCount: computed(() => {
      if (store.path() === PathType.CONFORT) {
        return (
          store.declaration().numberOfRooms!.numberOfRooms +
          (store.declaration().numberOfRooms!.between41And75 ?? 0) +
          (store.declaration().numberOfRooms!.moreThan76 === 1 ? 2 : 0) +
          (store.declaration().numberOfRooms!.hasVeranda === SpaceOfRoom.between10And40 ? 1 : 0)
        );
      } else {
        return (
          store.declaration().numberOfRooms!.numberOfRooms +
          (store.declaration().numberOfRooms!.hasVeranda === SpaceOfRoom.between10And40 ? 1 : 0)
        );
      }
    }),

    isWorkflowFromComparator: computed<boolean>(() => {
      if (isPartner(environment.brand)) return false;
      const partnersList = partners;
      return !!partnersList
        .filter((i) => i.partnerType === COMPARATOR)
        .some((i) => i.key === store.context().partnerCode);
    }),
  })),

  withComputed((store) => ({
    // DEPENDS ON OTHER STORE COMPUTED FUNCTION
    summaryAmount: computed(() => {
      return store
        .summary()
        .filter((item) => item.value !== 'Inclus')
        .reduce((total, item) => total + (Number(item.value) ?? 0), 0);
    }),
  })),
  withMethods((store) => ({
    select(): CombinedState {
      return getState(store);
    },
    updateContext(contextState: ContextState): void {
      patchState(store, (state) => ({
        context: { ...state.context, ...contextState },
      }));
    },
    updateContextSalesChannel(salesChannel: string): void {
      patchState(store, { context: { ...store.context(), originalCanalVente: salesChannel } });
    },
    updateTokenContext(token: string): void {
      patchState(store, (state) => ({
        context: { ...state.context, token: token },
      }));
    },
    resetContextState(): void {
      patchState(store, (state) => ({
        context: { ...state.context, ...initialContextState },
      }));
    },
    updatePreferentialRate(preferentialRate: boolean): void {
      patchState(store, (state) => ({
        declaration: {
          ...state.declaration,
          preferentialRate: preferentialRate,
        },
      }));
    },
    updatePackages(packages: Array<ProductPricesViewModel>): void {
      patchState(store, () => ({ packages }));
    },
    updatePath(path: PathType): void {
      patchState(store, (state) => ({
        path,
        declaration: {
          ...state.declaration,
          deductibleType:
            state.declaration.deductibleType === DeductibleType.CentCinquante
              ? getDefaultDeductibleTypeByPath(path)
              : state.declaration.deductibleType,
        },
      }));

      if (path === PathType.CONFORT && store.declaration().deductibleType !== DeductibleType.TroisCents) {
        this.updateDeclaration({ deductibleType: DeductibleType.TroisCents });
      }
    },

    cleanClimaticPeril(): void {
      patchState(store, (state) => ({
        climatic: {
          ...state.climatic,
          ClimaticPeril: [],
        },
      }));
    },
    updateClimaticPeril(climaticPeril: ClimaticPeril): void {
      const currentClimaticPerils = store.climatic().ClimaticPeril;
      const existingIndex = currentClimaticPerils.findIndex((peril) => peril.peril.code === climaticPeril.peril.code);
      const updatedClimaticPerils =
        existingIndex === -1
          ? [...currentClimaticPerils, climaticPeril]
          : currentClimaticPerils.map((peril, index) => (index === existingIndex ? climaticPeril : peril));
      patchState(store, (state) => ({
        climatic: {
          ...state.climatic,
          ClimaticPeril: updatedClimaticPerils,
        },
      }));
    },
    updateClimtaticAddressPrecise(isStreetNumberPrecise: boolean): void {
      patchState(store, {
        climatic: {
          ...store.climatic(),
          isStreetNumberPrecise,
        },
      });
    },
    updateSubscriberCG(subscriberCG: boolean): void {
      patchState(store, (state) => ({
        option_layout: { ...state.option_layout, subscriberCG: subscriberCG },
      }));
    },
    updateSchoolInsurance(schoolInsuranceState: SchoolInsuranceState): void {
      patchState(store, (state) => ({
        offer: {
          ...state.offer,
          options: {
            ...(state.offer.options ?? initialOptionState),
            schoolInsurance: {
              ...state.offer.options?.schoolInsurance,
              ...schoolInsuranceState,
            },
          },
        },
      }));
    },
    updateOptionItemsFromCoverages(
      optionalCoverages: { [key: string]: { [key: string]: number } } | null | undefined,
    ): void {
      const findAmount = (code: string): optionItemState | null => {
        const inner = optionalCoverages?.[code];
        if (!inner) return null;
        // Pour RCS, on prend la première valeur (prix par enfant)
        const amount = Math.round((Object.values(inner)[0] ?? 0) * 100) / 100;
        return code === RCS ? { children: [], isSelected: false, amount, code } : { isSelected: false, amount, code };
      };

      const rcsAmount = findAmount(RCS)?.amount ?? 0;
      patchState(store, (state) => ({
        offer: {
          ...state.offer,
          options: {
            ...(state.offer.options ?? initialOptionState),
            schoolInsurance: {
              ...(state.offer.options ?? initialOptionState).schoolInsurance,
              amount: rcsAmount,
            },
            theftAndVandalism: findAmount(VLX),
            brokenGlass: findAmount(BDG),
            electricalDamage: findAmount(DOMX),
            replacementValue: findAmount(VAN),
            landscaping: findAmount(OPT_AEXT),
          },
        },
      }));
    },
    updateSummary(summaryOffers: boolean): void {
      patchState(store, (state) => ({
        option_layout: { ...state.option_layout, summaryOffers: summaryOffers },
      }));
    },
    addRoommate(roommate: RiskItem | RiskItem[]): void {
      const roommates = Array.isArray(roommate) ? roommate : [roommate];
      patchState(store, (state) => ({
        declaration: { ...state.declaration, colocInformations: roommates },
      }));
    },
    updateNotifAttestationBailleur(notifAttestationBailleur: boolean): void {
      patchState(store, (state) => ({
        offer: {
          ...state.offer,
          lessorNotificationAttestation: notifAttestationBailleur,
        },
      }));
    },
    updateResiliationActualInsurer(resiliationActualInsurer: boolean): void {
      patchState(store, (state) => ({
        offer: { ...state.offer, insurerResiliation: resiliationActualInsurer },
      }));
    },
    updateSizeXL(sizeXL: boolean): void {
      patchState(store, (state) => ({
        option_layout: { ...state.option_layout, sizeXL: sizeXL },
      }));
    },
    updateHandlerContactPhone(contactPhone: string): void {
      patchState(store, () => ({ contactPhone }));
    },
    updateHeader(header: boolean): void {
      patchState(store, (state) => ({
        option_layout: { ...state.option_layout, header: header },
      }));
    },
    updateBreadcrumb(breadcrumb: boolean): void {
      patchState(store, (state) => ({
        option_layout: { ...state.option_layout, breadcrumb: breadcrumb },
      }));
    },
    updateWebcallback(webcallback: boolean): void {
      patchState(store, (state) => ({
        option_layout: { ...state.option_layout, webcallback: webcallback },
      }));
    },
    updateClimaticApi(climaticApi: boolean): void {
      patchState(store, (state) => ({
        option_layout: { ...state.option_layout, climaticApi: climaticApi },
      }));
    },
    updateClimaticData(climaticState: ClimaticState): void {
      patchState(store, (state) => ({
        climatic: { ...state.climatic, ...climaticState },
      }));
    },
    updateVerticalAlign(vertical_align: boolean): void {
      patchState(store, (state) => ({
        option_layout: {
          ...state.option_layout,
          vertical_align: vertical_align,
        },
      }));
    },
    updateStepCheckEmail(step_check_email: boolean): void {
      patchState(store, (state) => ({
        option_layout: {
          ...state.option_layout,
          step_check_email: step_check_email,
        },
      }));
    },
    updateFormErrorSubmit(form_error_submit: boolean): void {
      patchState(store, (state) => ({
        option_layout: {
          ...state.option_layout,
          form_error_submit: form_error_submit,
        },
      }));
    },
    updateWorkflowOutModalVisible(workflow_out_modal_visible: boolean): void {
      patchState(store, (state) => ({
        option_layout: {
          ...state.option_layout,
          workflow_out_modal_visible: workflow_out_modal_visible,
        },
      }));
    },
    updateOptionLayout(optionLayoutState: OptionLayoutState): void {
      patchState(store, (state) => ({
        option_layout: { ...state.option_layout, ...optionLayoutState },
      }));
    },
    resetOptionLayoutState(): void {
      patchState(store, (state) => ({
        option_layout: { ...state.option_layout, ...initialOptionLayoutState },
      }));
    },
    updateAddress(address: Address): void {
      patchState(store, (state) => ({
        declaration: { ...state.declaration, address: address },
      }));
    },
    updateDeclaration(declaration: Partial<DeclarationState>): void {
      patchState(store, (state) => ({
        declaration: { ...state.declaration, ...declaration },
      }));
    },
    resetDeclarationState(): void {
      patchState(store, (state) => ({
        declaration: { ...state.declaration, ...initialDeclarationState },
      }));
    },
    updateSubscriber(subscriberState: SubscriberState): void {
      patchState(store, (state) => ({
        subscriber: { ...state.subscriber, ...subscriberState },
      }));
    },
    updateEmailSubscriber(email: string): void {
      patchState(store, (state) => ({
        subscriber: { ...state.subscriber, email: email },
      }));
    },
    updateFromContextSubscriber(from_context: boolean): void {
      patchState(store, (state) => ({
        subscriber: { ...state.subscriber, from_context: from_context },
      }));
    },
    updateIsClientSubscriber(is_client: boolean): void {
      patchState(store, (state) => ({
        subscriber: { ...state.subscriber, is_client: is_client },
      }));
    },
    updatePhonePrefixSubscriber(phone_prefix: PhonePrefixState): void {
      patchState(store, (state) => ({
        subscriber: { ...state.subscriber, phone_prefix: phone_prefix },
      }));
    },
    resetSubscriberState(): void {
      patchState(store, (state) => ({
        subscriber: { ...state.subscriber, ...initialSubscriberState },
      }));
    },
    updateOffer(offerState: Partial<OfferState>): void {
      patchState(store, (state) => ({
        offer: { ...state.offer, ...offerState },
      }));
    },
    updateAdditionalInformationLessor(lessorAdditionalInformation: LessorAdditionalInformation): void {
      patchState(store, (state) => ({
        offer: {
          ...state.offer,
          lessorAdditionalInformation: lessorAdditionalInformation,
        },
      }));
    },
    updateLessor(lessor: Lessor): void {
      patchState(store, (state) => ({
        declaration: { ...state.declaration, lessor: lessor },
      }));
    },
    updateSelectedOffer(selected_offer: string): void {
      patchState(store, (state) => ({
        offer: { ...state.offer, selected_offer: selected_offer },
      }));
    },
    updateCouponCode(coupon_code: string): void {
      patchState(store, (state) => ({
        offer: { ...state.offer, coupon_code: coupon_code },
      }));
    },
    updateTypePromotion(type_promotion: string): void {
      patchState(store, (state) => ({
        offer: { ...state.offer, type_promotion: type_promotion },
      }));
    },
    resetCouponCode(): void {
      patchState(store, (state) => ({
        offer: { ...state.offer, coupon_code: '' },
      }));
    },
    updateMensualPrice(mensual_price: string): void {
      patchState(store, (state) => ({
        offer: { ...state.offer, mensual_price: mensual_price },
      }));
    },
    updateHighLighted(highlighted: boolean): void {
      patchState(store, (state) => ({
        offer: { ...state.offer, highlighted: highlighted },
      }));
    },
    updateSummaryOffer(pkge: PackageVM): void {
      patchState(store, (state) => ({
        summaryOffer: { ...state.summaryOffer, package: pkge },
      }));
    },
    updateOptions(options: KeyAmount[]): void {
      patchState(store, (state) => ({
        summaryOffer: { ...state.summaryOffer, options: options },
      }));
    },

    updateOfferOptions(option: Option): void {
      patchState(store, (state) => {
        const currentOptions = state.offer.options ?? initialOptionState;
        const optionKeys = [
          'theftAndVandalism',
          'brokenGlass',
          'electricalDamage',
          'replacementValue',
          'landscaping',
        ] as const;
        const updatedOptions = { ...currentOptions };
        for (const key of optionKeys) {
          const item = currentOptions[key];
          if (item?.code === option.code) {
            updatedOptions[key] = { ...item, isSelected: option.formControl.value };
            break;
          }
        }
        return { offer: { ...state.offer, options: updatedOptions } };
      });
    },
    updateReferenceDevis(reference_devis: string): void {
      patchState(store, (state) => ({
        offer: { ...state.offer, reference_devis: reference_devis },
      }));
    },
    resetOfferState(): void {
      patchState(store, (state) => ({
        offer: { ...state.offer, ...initialOfferState },
      }));
    },
    updateDirectDebit(directDebitState: DirectDebitState): void {
      patchState(store, (state) => ({
        direct_debit: { ...state.direct_debit, ...directDebitState },
      }));
    },
    updatePhonePrefixDirectDebit(phone_prefix: PhonePrefixState): void {
      patchState(store, (state) => ({
        direct_debit: { ...state.direct_debit, phone_prefix: phone_prefix },
      }));
    },
    resetDirectDebitState(): void {
      patchState(store, (state) => ({
        direct_debit: { ...state.direct_debit, ...initialDirectDebitState },
      }));
    },
    updateDevis(devisState: DevisState): void {
      patchState(store, (state) => ({
        devis: { ...state.devis, ...devisState },
      }));
    },
    updateDevisClientAdvisorFullName(name: string): void {
      patchState(store, (state) => ({
        devis: { ...state.devis, ClientAdvisorFullName: name },
      }));
    },
    updatePathType(pathType: PathType): void {
      patchState(store, (state) => ({
        path: pathType ?? state.path,
        declaration: {
          ...state.declaration,
          deductibleType:
            state.declaration.deductibleType === DeductibleType.CentCinquante
              ? getDefaultDeductibleTypeByPath(pathType ?? state.path)
              : state.declaration.deductibleType,
        },
      }));
    },
    resetDevisState(): void {
      patchState(store, (state) => ({
        devis: { ...state.devis, ...initialDevisState },
      }));
    },
    updateContract(contractState: ContractState): void {
      patchState(store, (state) => ({
        contract: { ...state.contract, ...contractState },
      }));
    },
    updateContractClientAdvisorFullName(name: string): void {
      patchState(store, (state) => ({
        contract: { ...state.contract, ClientAdvisorFullName: name },
      }));
    },
    updateContractId(numeroContrat: string): void {
      patchState(store, (state) => ({
        contract: { ...state.contract, NumeroContrat: numeroContrat },
      }));
    },
    updateContractReference(reference: string): void {
      patchState(store, (state) => ({
        contract: { ...state.contract, RefContrat: reference },
      }));
    },
    resetContractState(): void {
      patchState(store, (state) => ({
        contract: { ...state.contract, ...initialContractState },
      }));
    },
    updateElectronicSignature(electronicSignatureState: ElectronicSignatureState): void {
      patchState(store, (state) => ({
        electronic_signature: {
          ...state.electronic_signature,
          ...electronicSignatureState,
        },
      }));
    },
    resetElectronicSignatureState(): void {
      patchState(store, (state) => ({
        electronic_signature: {
          ...state.electronic_signature,
          ...initialElectronicSignatureState,
        },
      }));
    },
    updateSubscription(subscription: SubscriptionState | null): void {
      patchState(store, () => ({ subscription }));
    },
    resetSubscription(): void {
      patchState(store, (state) => ({
        subscription: { ...state.subscription, ...initialSubscriptionState },
      }));
    },
    updateForceAddressNavigation(value: boolean): void {
      patchState(store, { forceAddressNavigation: value });
    },
    update(combinedState: CombinedState): void {
      patchState(store, { ...combinedState });
    },
    reset(): void {
      patchState(store, { ...initialState });
    },
  })),
  withHooks({
    onInit(store) {
      effect(() => {
        saveState(store);
      });
    },
  }),
);

function getState(store: any): CombinedState {
  return {
    context: {
      token: store.context.token(),
      codeOperation: store.context.codeOperation(),
      modeConnexion: store.context.modeConnexion(),
      referenceDevis: store.context.referenceDevis(),
      branche: store.context.branche(),
      produit: store.context.produit(),
      canalVente: store.context.canalVente(),
      typeContact: store.context.typeContact(),
      referenceContrat: store.context.referenceContrat(),
      codeVendeur: store.context.codeVendeur(),
      avenants: store.context.avenants(),
      donneesPartenaire: store.context().donneesPartenaire,
      souscripteur: store.context().souscripteur,
      partnerCode: store.context().partnerCode,
      declaration: store.context().declaration,
    },
    path: store.path(),
    packages: store.packages(),
    contactPhone: store.contactPhone(),
    option_layout: {
      header: store.option_layout.header(),
      resume_step: store.option_layout.resume_step(),
      breadcrumb: store.option_layout.breadcrumb(),
      footer: store.option_layout.footer(),
      webcallback: store.option_layout.webcallback(),
      subscriberCG: store.option_layout.subscriberCG(),
      sizeXL: store.option_layout.sizeXL(),
      vertical_align: store.option_layout.vertical_align(),
      step_check_email: store.option_layout.step_check_email(),
      form_error_submit: store.option_layout.form_error_submit(),
      workflow_out_modal_visible: store.option_layout.workflow_out_modal_visible(),
      climaticApi: store.option_layout.climaticApi(),
      summaryOffers: store.option_layout.summaryOffers(),
    },
    declaration: {
      //MRH
      email: store.declaration.email(),

      address: store.declaration.address(),
      effect_date: store.declaration.effect_date() ? store.effect_date_formatted() : null,
      residenceType: store.declaration.residenceType(),
      deductibleType: store.declaration.deductibleType(),
      housingType: store.declaration.housingType(),
      dateHouseBuilted: store.declaration.dateHouseBuilted(),
      apartmentFloor: store.declaration.apartmentFloor(),
      qualiteOccupant: store.declaration.qualiteOccupant(),
      nbColoc: store.declaration.nbColoc(),
      nbPersonInHome: store.declaration.nbPersonInHome(),
      lessor: store.declaration.lessor(),
      lessorType: store.declaration.lessorType(),
      subscriberHasBeenCanceled: store.declaration.subscriberHasBeenCanceled(),
      susbcriberHadClaimsInLast3Years: store.declaration.susbcriberHadClaimsInLast3Years(),
      numberOfRooms: store.declaration.numberOfRooms(),
      dependances: store.declaration.dependances(),
      durableDevelopmentInstallation: store.declaration.durableDevelopmentInstallation(),
      hasSwimmingPoolSpaOrJacuzzi: store.declaration.hasSwimmingPoolSpaOrJacuzzi(),
      capitalMobilier: store.declaration.capitalMobilier(),
      preferentialRate: store.declaration.preferentialRate(),
      colocInformations: store.declaration.colocInformations(),
      movableHeritage: store.declaration.movableHeritage(),
      objetValue: store.declaration.objetValue(),
      typeOfBedroom: store.declaration.typeOfBedroom(),
      revenus: store.declaration.revenus(),
    },
    subscriber: {
      email: store.subscriber.email(),
      civility: store.subscriber.civility(),
      lastname: store.subscriber.lastname(),
      firstname: store.subscriber.firstname(),
      birth_date: store.subscriber.birth_date() ? store.birth_date_subscriber_formatted() : null,
      birth_postal_code: store.subscriber.birth_postal_code(),
      birth_country: store.subscriber.birth_country(),
      birth_city: store.subscriber.birth_city(),
      nationality: store.subscriber.nationality(),
      phone_prefix: store.subscriber.phone_prefix(),
      phone_number: store.subscriber.phone_number(),
      consent: store.subscriber.consent(),
      is_client: store.subscriber.is_client(),
      from_context: store.subscriber.from_context(),
    },
    offer: {
      selected_offer: store.offer.selected_offer(),
      coupon_code: store.offer.coupon_code(),
      mensual_price: store.offer.mensual_price(),
      highlighted: store.offer.highlighted(),
      franchises: store.offer.franchises(),
      reference_devis: store.offer.reference_devis(),
      type_promotion: store.offer.type_promotion(),
      renforcement: store.offer.renforcement(),
      options: store.offer.options(),
      sponsor_email: store.offer.sponsor_email(),
      insurerResiliation: store.offer.insurerResiliation(),
      lessorNotificationAttestation: store.offer.lessorNotificationAttestation(),
      resiliationConcurrence: store.offer.resiliationConcurrence(),
      lessorAdditionalInformation: store.offer.lessorAdditionalInformation(),
      policyInsuranceActual: store.offer.policyInsuranceActual(),
      resiliationAssureur: store.offer.resiliationAssureur(),
    },
    direct_debit: {
      periodicity: store.direct_debit.periodicity(),
      start_date: store.direct_debit.start_date() ? store.start_date_direct_debit_formatted() : null,
      debit_day: store.direct_debit.debit_day(),
      iban: store.direct_debit.iban(),
      account_owner: store.direct_debit.account_owner(),
      phone_prefix: store.direct_debit.phone_prefix(),
    },
    devis: store.devis(),
    contract: store.contract(),
    electronic_signature: store.electronic_signature(),
    subscription: store.subscription(),
    climatic: store.climatic(),
    summaryOffer: store.summaryOffer(),
    forceAddressNavigation: store.forceAddressNavigation(),
  };
}

function saveState(store: any) {
  try {
    saveStateInLocalStorage(getState(store));
  } catch (error) {
    console.error('Error saving state, sessionStorage unchanged:', error);
  }
}

function saveStateInLocalStorage(obj: CombinedState) {
  sessionStorage.setItem('combinedState', JSON.stringify(obj));
}

function getStateFromLocalStorage(): CombinedState {
  const state = sessionStorage.getItem('combinedState');
  return state ? JSON.parse(state) : initialState;
}
